import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, Shield, TrendingUp, Building2, Phone, Maximize, CalendarDays, CheckCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";
import { useSettings } from "@/hooks/useSettings";

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LocalBusiness", "RealEstateAgent"],
      "@id": "https://www.brimo-immobilien-kreismettmann.de/#business",
      name: "BRIMO Immobilien NRW",
      alternateName: "Bricks and Mortar Immobilien Nordrhein-Westfalen GmbH",
      url: "https://www.brimo-immobilien-kreismettmann.de",
      telephone: "+4921293766829",
      email: "info@brimo-immobilien-kreismettmann.de",
      image: cityConfig.manager.photo,
      logo: cityConfig.manager.photo,
      priceRange: "$$",
      currenciesAccepted: "EUR",
      paymentAccepted: "Cash, Bank Transfer",
      openingHours: "Mo-Fr 09:00-18:00",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Wilhelmstraße 3",
        addressLocality: "Haan",
        postalCode: "42781",
        addressRegion: "Nordrhein-Westfalen",
        addressCountry: "DE",
      },
      geo: { "@type": "GeoCoordinates", latitude: 51.2500, longitude: 6.9747 },
      areaServed: {
        "@type": "AdministrativeArea",
        name: "Kreis Mettmann",
        sameAs: "https://de.wikipedia.org/wiki/Kreis_Mettmann",
      },
      knowsAbout: ["Immobilienverkauf", "Immobilienkauf", "Immobilienbewertung", "Maklerservice"],
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": "https://www.brimo-immobilien-kreismettmann.de/#website",
      url: "https://www.brimo-immobilien-kreismettmann.de",
      name: "BRIMO Immobilien NRW",
      description: cityConfig.seo.defaultDescription,
      publisher: { "@id": "https://www.brimo-immobilien-kreismettmann.de/#business" },
      inLanguage: "de-DE",
    },
  ],
};

export default function HomePage() {
  useMeta(
    "Immobilienmakler Kreis Mettmann | BRIMO Immobilien",
    "BRIMO Immobilien NRW – Ihr professioneller Immobilienmakler. Fundierte Immobilienbewertung, erfolgreicher Verkauf, kompetente Beratung für Käufer."
  );
  useSchema(schema);

  const [objekte, setObjekte] = useState([]);
  const settings = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (settings.objekte_enabled !== false) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/objekte`).then((r) => {
        const data = r.data;
        if (data.length > 1) {
          const offset = Math.floor(Math.random() * data.length);
          setObjekte([...data.slice(offset), ...data.slice(0, offset)]);
        } else {
          setObjekte(data);
        }
      }).catch(() => {});
    }
  }, [settings.objekte_enabled]);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col justify-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={cityConfig.images.hero}
            alt="Kreis Mettmann – BRIMO Immobilien NRW"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/70" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-24 pb-12">
          <div className="max-w-3xl">
            <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
            <p className="text-white text-sm sm:text-base uppercase tracking-[0.3em] font-bold mb-5 drop-shadow-lg">
              Ihr Immobilienmakler im Kreis Mettmann
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              IMMOBILIEN
              <br />
              <span className="text-[#E61D38]">WERDEN</span>
              <br />
              HIER ENTSCHIEDEN
            </h1>
            <p className="text-gray-300 text-base md:text-lg max-w-xl leading-relaxed mb-8">
              Ob Verkauf oder Kauf – wir begleiten Sie im Kreis Mettmann mit klarer Strategie, fundierter Marktkenntnis und persönlicher Beratung.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/marktwertermittlung-kreis-mettmann"
                className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors"
                data-testid="hero-sell-cta"
              >
                Kostenlose Bewertung
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/immobilie-kaufen-kreis-mettmann"
                className="flex items-center gap-2 border border-white/30 text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:border-white/60 transition-colors"
                data-testid="hero-buy-cta"
              >
                Immobilie kaufen
                <ArrowRight size={16} />
              </Link>
            </div>

            {/* Trust Signals */}
            <div className="flex flex-wrap items-center gap-8 border-t border-white/10 pt-8" data-testid="hero-trust-signals">
              <div className="flex items-center gap-4">
                <img
                  src={cityConfig.manager.photo}
                  alt={cityConfig.manager.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#E61D38]"
                  loading="eager"
                />
                <div>
                  <p className="text-white text-base font-bold">{cityConfig.manager.name}</p>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">{cityConfig.manager.title}</p>
                </div>
              </div>
              <div className="h-10 w-px bg-white/15 hidden sm:block" />
              <div className="flex items-center gap-2.5">
                <MapPin size={16} className="text-[#E61D38]" />
                <p className="text-gray-300 text-sm">{cityConfig.address.street}, {cityConfig.address.postalCode} {cityConfig.address.city}</p>
              </div>
              <div className="h-10 w-px bg-white/15 hidden sm:block" />
              <a href={`tel:${cityConfig.phone}`} className="flex items-center gap-2.5 text-gray-300 text-sm hover:text-[#E61D38] transition-colors" data-testid="hero-phone">
                <Phone size={16} className="text-[#E61D38]" />
                {cityConfig.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Blocks */}
      <section data-testid="decision-blocks">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Seller Block */}
          <Link
            to="/immobilie-verkaufen-kreis-mettmann"
            className="group relative overflow-hidden min-h-[420px] flex flex-col justify-end"
            data-testid="seller-routing-block"
          >
            <div className="absolute inset-0">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/42ada0df-a68a-464a-87b2-c661f7b68b9a/images/7d6629f9672c53effdb91fd1640180f1cf61334c2fc622cac79c4293b5446b3a.png"
                alt="Immobilie verkaufen im Kreis Mettmann"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/70 to-[#1a1a1a]/50 group-hover:via-[#0d0d0d]/60 group-hover:to-[#1a1a1a]/35 transition-colors duration-500" />
              <div className="absolute inset-y-0 right-0 w-px bg-white/5" />
            </div>
            <div className="relative z-10 p-8 md:p-12">
              <div className="h-0.5 w-0 bg-[#E61D38] mb-4 transition-all duration-500 group-hover:w-12" />
              <p className="text-[#E61D38] text-sm uppercase tracking-[0.25em] font-bold mb-3 drop-shadow-lg">Für Eigentümer</p>
              <h2
                className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-none mb-3"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Immobilie verkaufen
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-xs">
                Kostenlose Bewertung, professionelle Vermarktung und Begleitung bis zum Notartermin.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#E61D38] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 group-hover:bg-[#c41930] transition-colors">
                <span>Jetzt beraten lassen</span>
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </Link>

          {/* Buyer Block */}
          <Link
            to="/immobilie-kaufen-kreis-mettmann"
            className="group relative overflow-hidden min-h-[420px] flex flex-col justify-end"
            data-testid="buyer-routing-block"
          >
            <div className="absolute inset-0">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/42ada0df-a68a-464a-87b2-c661f7b68b9a/images/d1ebd045c92e9b24408ad673dfd8f4ed7a5de0f6a93a81e784076fe8660d0de2.png"
                alt="Immobilie kaufen im Kreis Mettmann"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/70 to-[#1a1a1a]/50 group-hover:via-[#0d0d0d]/60 group-hover:to-[#1a1a1a]/35 transition-colors duration-500" />
            </div>
            <div className="relative z-10 p-8 md:p-12">
              <div className="h-0.5 w-0 bg-[#E61D38] mb-4 transition-all duration-500 group-hover:w-12" />
              <p className="text-[#E61D38] text-sm uppercase tracking-[0.25em] font-bold mb-3 drop-shadow-lg">Für Kaufinteressenten</p>
              <h2
                className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-none mb-3"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Immobilie kaufen
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-xs">
                Ihre Wunschimmobilie im Kreis Mettmann finden – mit persönlicher Beratung und Marktexpertise.
              </p>
              <div className="inline-flex items-center gap-2 bg-[#E61D38] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 group-hover:bg-[#c41930] transition-colors">
                <span>Angebote entdecken</span>
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-14 bg-[#0d0d0d]" data-testid="trust-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { Icon: MapPin, label: "Lokale Expertise", desc: "Tief verwurzelt im Kreis Mettmann" },
              { Icon: Shield, label: "Vertrauenswürdig", desc: "Diskretion & Transparenz" },
              { Icon: TrendingUp, label: "Marktkenntnis", desc: "Optimale Preispositionierung" },
              { Icon: Building2, label: "Professionell", desc: "Von Bewertung bis Notar" },
            ].map(({ Icon, label, desc }, i) => (
              <div key={i} className="text-center" data-testid={`trust-item-${i}`}>
                <div className="flex justify-center mb-3">
                  <Icon size={26} className="text-[#E61D38]" />
                </div>
                <p className="text-white font-bold text-xs uppercase tracking-wider mb-1.5">
                  {label}
                </p>
                <p className="text-gray-500 text-xs">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objekte & Referenzen */}
      {settings.objekte_enabled !== false && objekte.length > 0 && (
        <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="objekte-section">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2
                  className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Objekte & Referenzen
                </h2>
                <p className="text-gray-400 text-sm max-w-lg leading-relaxed">
                  Aktuelle Verkaufsobjekte und erfolgreich vermittelte Immobilien im Kreis Mettmann.
                </p>
              </div>
              <Link
                to="/objekte"
                className="hidden md:flex items-center gap-2 text-[#E61D38] text-xs font-bold uppercase tracking-wider hover:text-white transition-colors"
                data-testid="objekte-section-link"
              >
                Alle ansehen <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {objekte.slice(0, 6).map((obj) => {
                const isSold = obj.status === "verkauft";
                const img = obj.images?.[0];
                return (
                  <Link
                    key={obj.id}
                    to={`/objekte/${obj.slug}`}
                    className="group bg-[#0d0d0d] border border-white/5 overflow-hidden hover:border-[#E61D38]/30 transition-colors relative"
                    data-testid={`home-objekt-${obj.slug}`}
                  >
                    {isSold && (
                      <div className="absolute top-3 right-3 z-10 bg-emerald-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle size={10} /> Verkauft
                      </div>
                    )}
                    {img && (
                      <div className="relative h-52 overflow-hidden">
                        <img src={img} alt={obj.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] to-transparent opacity-35" />
                        {obj.price && (
                          <div className="absolute bottom-3 left-3 bg-[#0d0d0d]/80 backdrop-blur-sm text-white text-sm font-bold px-3 py-1.5 border border-white/10">
                            {obj.price}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-white font-bold text-sm mb-2 group-hover:text-[#E61D38] transition-colors leading-snug">
                        {obj.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mb-3">
                        {obj.district && <span className="flex items-center gap-1"><MapPin size={9} /> {obj.district}</span>}
                        {obj.property_type && <span className="flex items-center gap-1"><Building2 size={9} /> {obj.property_type}</span>}
                        {obj.living_area && <span className="flex items-center gap-1"><Maximize size={9} /> {obj.living_area}</span>}
                        {obj.year_built && <span className="flex items-center gap-1"><CalendarDays size={9} /> Bj. {obj.year_built}</span>}
                      </div>
                      <span className="flex items-center gap-1 text-[#E61D38] text-[10px] font-bold uppercase tracking-wider">
                        {isSold ? "Referenz ansehen" : "Details"} <ArrowRight size={9} className="transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-center md:hidden">
              <Link to="/objekte" className="inline-flex items-center gap-2 text-[#E61D38] text-xs font-bold uppercase tracking-wider">
                Alle Objekte ansehen <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* About / Local Presence */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="about-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                BRIMO IMMOBILIEN
                <br />
                NRW
              </h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Als lokaler Immobilienmakler im Kreis Mettmann kennen wir den Markt wie kaum ein anderer.
                Wir sind fest in der Region verwurzelt und begleiten Sie persönlich durch den
                gesamten Prozess – ob Verkauf oder Kauf.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Mit fundierter Marktanalyse, einer starken Vermarktungsstrategie und einem
                strukturierten Prozess erzielen wir für unsere Kunden optimale Ergebnisse in
                Hilden, Ratingen, Langenfeld, Haan und Erkrath.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/marktwertermittlung-kreis-mettmann"
                  className="flex items-center gap-2 bg-[#E61D38] text-white px-6 py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors"
                  data-testid="about-markttwert-cta"
                >
                  Marktwert ermitteln
                  <ArrowRight size={14} />
                </Link>
                <a
                  href={`tel:${cityConfig.phone}`}
                  className="flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 font-bold uppercase tracking-wider text-sm hover:border-white/50 transition-colors"
                  data-testid="about-phone-cta"
                >
                  <Phone size={14} />
                  Anrufen
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={cityConfig.images.office}
                alt="BRIMO Immobilien NRW – Kreis Mettmann"
                className="w-full h-80 md:h-[420px] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#E61D38] p-5">
                <p className="text-white font-bold text-sm uppercase tracking-wider">
                  Wilhelmstraße 3
                </p>
                <p className="text-white/80 text-xs mt-0.5">42781 Haan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Districts Grid */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="districts-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            UNSERE STÄDTE
          </h2>
          <p className="text-gray-400 mb-12 max-w-xl text-sm leading-relaxed">
            In diesen Städten im Kreis Mettmann sind wir aktiv und kennen den lokalen
            Immobilienmarkt in- und auswendig.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {cityConfig.districts.map((district) => (
              <Link
                key={district.slug}
                to={`/immobilie-verkaufen-${district.slug}`}
                className="group bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/50 transition-all duration-300 hover:-translate-y-0.5"
                data-testid={`district-${district.slug}`}
              >
                <div className="h-0.5 w-0 bg-[#E61D38] mb-3 transition-all duration-300 group-hover:w-8" />
                <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">
                  {district.name}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">
                  {district.description.substring(0, 55)}...
                </p>
                <div className="mt-4 flex items-center gap-1 text-[#E61D38] text-xs font-bold uppercase tracking-wider">
                  <span>Mehr</span>
                  <ArrowRight
                    size={10}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial / Award Section */}
      <section className="py-14 bg-[#1a1a1a] border-y border-white/5" data-testid="awards-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_c18fba98-66cc-4e36-b9db-e24324823938/artifacts/xshmmzta_IMMOPREISGEWINNER.png"
                alt="Deutscher Immobilienpreis Winner 2023 – immowelt"
                className="h-24 md:h-28 w-auto border border-white/10 flex-shrink-0"
                data-testid="award-logo"
                loading="lazy"
              />
              <div>
                <p className="text-white font-bold text-sm uppercase tracking-widest">
                  Deutscher Immobilienpreis
                </p>
                <p className="text-gray-500 text-xs mt-1">WINNER 2023 – Ausgezeichnetes Maklerunternehmen</p>
              </div>
            </div>
            <div className="h-px md:h-12 w-full md:w-px bg-white/10" />
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm italic max-w-md">
                "Immobilien werden hier entschieden. Gemeinsam ankommen."
              </p>
              <p className="text-[#E61D38] font-bold text-xs uppercase tracking-widest mt-2">
                BRIMO Immobilien NRW
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-16 bg-[#E61D38]" data-testid="cta-strip">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2
            className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-3"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            JETZT ANFRAGEN UND BASIS SCHAFFEN
          </h2>
          <p className="text-white/80 text-sm mb-8 max-w-lg mx-auto">
            Persönlicher Ansprechpartner. Diskret & unverbindlich. Schnelle Rückmeldung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/marktwertermittlung-kreis-mettmann"
              className="bg-white text-[#E61D38] px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gray-100 transition-colors"
              data-testid="cta-markttwert"
            >
              Marktwert ermitteln
            </Link>
            <a
              href={`tel:${cityConfig.phone}`}
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-white/10 transition-colors"
              data-testid="cta-phone"
            >
              {cityConfig.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
